import { useState, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MinusIcon,
  PlusIcon,
  Squares2X2Icon,
  Bars3Icon,
  ExclamationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';
import { Select } from '../../tailwindComponents/Select';
import { ProductItem, LoadSummary, palletTypeOptions } from './types';

// Mock product data
const mockProducts: ProductItem[] = [
  {
    id: '1',
    skuId: '4395-is',
    name: 'Ananas',
    image: '/images/products/ananas.png',
    inStock: 50,
    totalQty: 50,
    weight: 50,
    selectedQty: 50,
    palletType: 'EU Pallet',
    isSelected: true,
    isExpanded: true,
    warehouseStock: [
      {
        warehouseId: 'w1',
        warehouseName: 'Unit A Ambient',
        storedQty: 543,
        address: 'Fairway DR, Greenford UB6 8PW, UK',
        availability: 'Available',
        status: 'Active',
      },
      {
        warehouseId: 'w2',
        warehouseName: 'Unit B Ambient',
        storedQty: 334,
        address: 'Greenford UB6 8PW, UK',
        availability: 'Available',
        status: 'Non-engaged',
      },
      {
        warehouseId: 'w3',
        warehouseName: 'Unit C',
        storedQty: 57,
        address: 'Fairway DR, Greenford UB6 8PW, UK',
        availability: 'Available',
        status: 'Inactive',
      },
    ],
  },
  {
    id: '2',
    skuId: '546447',
    name: 'Coca Cola Classic',
    image: '/images/products/coca-cola.png',
    inStock: 50,
    totalQty: 50,
    weight: 50,
    selectedQty: 0,
    palletType: 'EU Pallet',
    isSelected: true,
    isExpanded: false,
    isUnavailable: true,
    warehouseStock: [
      {
        warehouseId: 'w3',
        warehouseName: 'Unit C',
        storedQty: 0,
        address: 'Fairway DR, Greenford UB6 8PW, UK',
        availability: 'N/A',
        status: 'Quarantined',
      },
    ],
  },
  {
    id: '3',
    skuId: '789012',
    name: 'Nouri gift packege',
    image: '/images/products/nouri.png',
    inStock: 50,
    totalQty: 50,
    weight: 0,
    selectedQty: 0,
    palletType: 'EU Pallet',
    isSelected: false,
    isExpanded: false,
    warehouseStock: [],
  },
  {
    id: '4',
    skuId: '345678',
    name: 'Pack of chips',
    image: '/images/products/chips.png',
    inStock: 50,
    totalQty: 50,
    weight: 0,
    selectedQty: 0,
    palletType: 'EU Pallet',
    isSelected: false,
    isExpanded: false,
    warehouseStock: [],
  },
];

interface ProductsLoadStepProps {
  products: ProductItem[];
  onProductsChange: (products: ProductItem[]) => void;
}

function getPalletTypeName(type: string): string {
  return (
    palletTypeOptions.find((option) => option.value === type)?.label ?? type
  );
}

function StatusBadge({ status }: { status: string }) {
  const getStatusStyles = () => {
    switch (status) {
      case 'Active':
        return 'bg-blue-100 text-blue-700';
      case 'Non-engaged':
        return 'bg-gray-100 text-gray-600';
      case 'Inactive':
        return 'bg-orange-100 text-orange-600';
      case 'Quarantined':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <span
      className={twMerge(
        'px-2 py-1 rounded-full text-xs font-medium',
        getStatusStyles()
      )}
    >
      {status}
    </span>
  );
}

function AvailabilityBadge({ availability }: { availability: string }) {
  return (
    <span
      className={twMerge(
        'px-2 py-1 rounded text-xs font-medium',
        availability === 'Available'
          ? 'bg-green-100 text-green-700'
          : 'bg-red-100 text-red-600'
      )}
    >
      {availability}
    </span>
  );
}

export function ProductsLoadStep({
  products: initialProducts,
  onProductsChange,
}: ProductsLoadStepProps) {
  const [products, setProducts] = useState<ProductItem[]>(
    initialProducts.length > 0 ? initialProducts : mockProducts
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [warehouseModalProduct, setWarehouseModalProduct] =
    useState<ProductItem | null>(null);

  // Calculate load summary
  const loadSummary: LoadSummary = useMemo(() => {
    const selectedProducts = products.filter((p) => p.isSelected);
    const totalUnits = selectedProducts.reduce(
      (sum, p) => sum + p.selectedQty,
      0
    );

    // Calculate pallet breakdown by type
    const palletsByType: Record<
      string,
      { quantity: number; weight: number; volume: number }
    > = {};

    selectedProducts.forEach((product) => {
      if (!palletsByType[product.palletType]) {
        palletsByType[product.palletType] = {
          quantity: 0,
          weight: 0,
          volume: 0,
        };
      }
      // Simplified: 1 pallet per 10 units
      const palletCount = Math.ceil(product.selectedQty / 10);
      palletsByType[product.palletType].quantity += palletCount;
      palletsByType[product.palletType].weight += product.weight;
      // Simplified volume calculation
      palletsByType[product.palletType].volume += palletCount * 1.0;
    });

    const palletBreakdown = Object.entries(palletsByType).map(
      ([type, data]) => {
        const palletOption = palletTypeOptions.find((p) => p.value === type);
        return {
          type,
          dimensions: palletOption?.dimensions || '1200×800 mm',
          quantity: data.quantity,
          weight: data.weight,
          volume: data.volume,
        };
      }
    );

    const totalPallets = palletBreakdown.reduce(
      (sum, p) => sum + p.quantity,
      0
    );
    const totalWeight = selectedProducts.reduce((sum, p) => sum + p.weight, 0);
    const unavailableProducts = selectedProducts.filter((p) => p.isUnavailable);

    return {
      totalSKUs: selectedProducts.length,
      totalUnits,
      totalPallets,
      palletBreakdown,
      totalWeight,
      unavailableProducts,
    };
  }, [products]);

  const handleToggleSelect = (productId: string) => {
    const updatedProducts = products.map((p) =>
      p.id === productId ? { ...p, isSelected: !p.isSelected } : p
    );
    setProducts(updatedProducts);
    onProductsChange(updatedProducts);
  };

  const handleToggleExpand = (productId: string) => {
    setProducts(
      products.map((p) =>
        p.id === productId ? { ...p, isExpanded: !p.isExpanded } : p
      )
    );
  };

  const handleQuantityChange = (productId: string, delta: number) => {
    const updatedProducts = products.map((p) => {
      if (p.id === productId) {
        const newQty = Math.max(0, Math.min(p.inStock, p.selectedQty + delta));
        return { ...p, selectedQty: newQty };
      }
      return p;
    });
    setProducts(updatedProducts);
    onProductsChange(updatedProducts);
  };

  const handlePalletTypeChange = (productId: string, palletType: string) => {
    const updatedProducts = products.map((p) =>
      p.id === productId ? { ...p, palletType } : p
    );
    setProducts(updatedProducts);
    onProductsChange(updatedProducts);
  };

  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedCount = products.filter((p) => p.isSelected).length;

  return (
    <div className='grid grid-cols-[1fr_320px] gap-6'>
      {/* Left Column - SKUs List */}
      <div className='flex flex-col gap-4'>
        {/* Header */}
        <div>
          <h2 className='text-lg font-semibold text-gray-900'>SKUs Load</h2>
          <p className='text-sm text-gray-500'>
            Select SKUs and provide its quantities.
          </p>
        </div>

        {/* Filters Row */}
        <div className='flex items-end gap-2'>
          {/* Search - fixed width */}
          <div className='relative w-48'>
            <MagnifyingGlassIcon className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400' />
            <input
              type='text'
              placeholder='Search...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className='w-full h-8 pl-9 pr-3 text-sm border border-gray-200 rounded-md shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-blue-200 focus:outline-none bg-white'
            />
          </div>

          {/* Categories */}
          <div className='flex-1 min-w-0'>
            <Select
              options={[
                { label: 'All categories', value: 'all' },
                { label: 'Food & Beverage', value: 'food' },
                { label: 'Electronics', value: 'electronics' },
              ]}
              selected={{ label: 'All categories', value: categoryFilter }}
              setSelected={(opt) => setCategoryFilter(String(opt.value))}
              sizeVariant='sm'
            />
          </div>

          {/* Warehouses */}
          <div className='flex-1 min-w-0'>
            <Select
              options={[
                { label: 'All warehouses', value: 'all' },
                { label: 'Unit A Ambient', value: 'w1' },
                { label: 'Unit B Ambient', value: 'w2' },
                { label: 'Unit C', value: 'w3' },
              ]}
              selected={{ label: 'All warehouses', value: warehouseFilter }}
              setSelected={(opt) => setWarehouseFilter(String(opt.value))}
              sizeVariant='sm'
            />
          </div>

          {/* View Mode Toggle */}
          <div className='flex items-center h-8 border border-gray-200 rounded-md shadow-sm ring-1 ring-inset ring-gray-200 overflow-hidden'>
            <button
              type='button'
              onClick={() => setViewMode('list')}
              className={twMerge(
                'h-full px-2 flex items-center justify-center',
                viewMode === 'list'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              )}
            >
              <Bars3Icon className='h-4 w-4' />
            </button>
            <button
              type='button'
              onClick={() => setViewMode('grid')}
              className={twMerge(
                'h-full px-2 flex items-center justify-center',
                viewMode === 'grid'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-500 hover:bg-gray-50'
              )}
            >
              <Squares2X2Icon className='h-4 w-4' />
            </button>
          </div>
        </div>

        {/* Products List View */}
        {viewMode === 'list' && (
          <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
            {/* Table Header */}
            <div className='grid grid-cols-[40px_1fr_60px_100px_100px_120px_100px] gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500'>
              <div />
              <div>Name</div>
              <div>ID</div>
              <div className='text-center'>In stock</div>
              <div className='text-center'>Total weight</div>
              <div className='text-center'>SKUs qty</div>
              <div>Pallet type</div>
            </div>

            {/* Product Rows */}
            {filteredProducts.map((product) => (
              <div key={product.id} className='border-b border-gray-100'>
                {/* Main Row */}
                <div className='grid grid-cols-[40px_1fr_60px_100px_100px_120px_100px] gap-2 px-4 py-3 items-center'>
                  {/* Checkbox */}
                  <div>
                    <input
                      type='checkbox'
                      checked={product.isSelected}
                      onChange={() => handleToggleSelect(product.id)}
                      className='h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary'
                    />
                  </div>

                  {/* Name with image and expand toggle */}
                  <div className='flex items-center gap-3'>
                    <button
                      type='button'
                      onClick={() => handleToggleExpand(product.id)}
                      className='text-gray-400 hover:text-gray-600'
                    >
                      {product.isExpanded ? (
                        <ChevronUpIcon className='h-4 w-4' />
                      ) : (
                        <ChevronDownIcon className='h-4 w-4' />
                      )}
                    </button>
                    <div className='h-10 w-10 bg-gray-100 rounded flex items-center justify-center overflow-hidden'>
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.name}
                          className='h-full w-full object-cover'
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              'none';
                          }}
                        />
                      ) : (
                        <span className='text-xs text-gray-400'>IMG</span>
                      )}
                    </div>
                    <span className='font-medium text-gray-900'>
                      {product.name}
                    </span>
                  </div>

                  {/* ID */}
                  <div className='text-sm text-gray-500'>{product.skuId}</div>

                  {/* In Stock */}
                  <div className='text-sm text-gray-900 text-center'>
                    {product.inStock}
                  </div>

                  {/* Total Weight */}
                  <div className='text-sm text-gray-900 text-center'>
                    {product.weight}
                  </div>

                  {/* SKU Qty with +/- controls */}
                  <div className='flex items-center justify-center gap-1'>
                    <button
                      type='button'
                      onClick={() => handleQuantityChange(product.id, -1)}
                      className='p-1 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50'
                      disabled={product.selectedQty <= 0}
                    >
                      <MinusIcon className='h-3 w-3' />
                    </button>
                    <input
                      type='text'
                      value={product.selectedQty}
                      readOnly
                      className='w-12 text-center text-sm border border-gray-300 rounded py-1'
                    />
                    <button
                      type='button'
                      onClick={() => handleQuantityChange(product.id, 1)}
                      className='p-1 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50'
                      disabled={product.selectedQty >= product.inStock}
                    >
                      <PlusIcon className='h-3 w-3' />
                    </button>
                  </div>

                  {/* Pallet Type */}
                  <div>
                    <select
                      value={product.palletType}
                      onChange={(e) =>
                        handlePalletTypeChange(product.id, e.target.value)
                      }
                      className='w-full text-sm border border-gray-300 rounded py-1 px-2'
                    >
                      {palletTypeOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Expanded Warehouse Details */}
                {product.isExpanded && product.warehouseStock.length > 0 && (
                  <div className='bg-gray-50 px-4 py-3 ml-10'>
                    {/* Warehouse Table Header */}
                    <div className='grid grid-cols-[1fr_100px_1fr_100px_100px] gap-4 text-xs font-medium text-gray-500 mb-2'>
                      <div className='flex items-center gap-1'>
                        Warehouse
                        <ChevronDownIcon className='h-3 w-3' />
                      </div>
                      <div className='flex items-center gap-1'>
                        Stored qty
                        <ChevronDownIcon className='h-3 w-3' />
                      </div>
                      <div className='flex items-center gap-1'>
                        Address
                        <ChevronDownIcon className='h-3 w-3' />
                      </div>
                      <div className='flex items-center gap-1'>
                        Availability
                        <ChevronDownIcon className='h-3 w-3' />
                      </div>
                      <div className='flex items-center gap-1'>
                        Status
                        <ChevronDownIcon className='h-3 w-3' />
                      </div>
                    </div>

                    {/* Warehouse Rows */}
                    {product.warehouseStock.map((warehouse) => (
                      <div
                        key={warehouse.warehouseId}
                        className='grid grid-cols-[1fr_100px_1fr_100px_100px] gap-4 py-2 border-t border-gray-200 text-sm'
                      >
                        <div className='text-gray-900'>
                          {warehouse.warehouseName}
                        </div>
                        <div className='text-gray-900'>
                          {warehouse.storedQty}
                        </div>
                        <div className='text-gray-500'>{warehouse.address}</div>
                        <div>
                          <AvailabilityBadge
                            availability={warehouse.availability}
                          />
                        </div>
                        <div>
                          <StatusBadge status={warehouse.status} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Products Grid View */}
        {viewMode === 'grid' && (
          <div className='grid grid-cols-3 gap-4'>
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className={twMerge(
                  'bg-white rounded-lg border overflow-hidden',
                  product.isSelected ? 'border-primary' : 'border-gray-200'
                )}
              >
                {/* Product Image with checkbox */}
                <div className='relative aspect-square bg-gray-100'>
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className='w-full h-full object-cover'
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className='w-full h-full flex items-center justify-center text-gray-400'>
                      No image
                    </div>
                  )}
                  {/* Checkbox overlay */}
                  <div className='absolute top-2 left-2'>
                    <input
                      type='checkbox'
                      checked={product.isSelected}
                      onChange={() => handleToggleSelect(product.id)}
                      className='h-5 w-5 text-primary border-gray-300 rounded focus:ring-primary bg-white'
                    />
                  </div>
                  {/* Unavailable overlay */}
                  {product.isUnavailable && (
                    <div className='absolute bottom-2 right-2'>
                      <span className='bg-red-500 text-white text-xs px-2 py-1 rounded-full'>
                        Unavailable
                      </span>
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className='p-3 space-y-3'>
                  {/* Name */}
                  <h4 className='font-medium text-gray-900 text-sm'>
                    {product.name}
                  </h4>

                  {/* Stats row */}
                  <div className='flex items-center justify-between text-xs text-gray-500'>
                    <span>SKU ID</span>
                    <span className='text-gray-900'>{product.skuId}</span>
                  </div>
                  <div className='flex items-center justify-between text-xs text-gray-500'>
                    <span>In stock qty</span>
                    <span className='text-gray-900'>{product.inStock}</span>
                  </div>

                  {/* Controls - only show when selected */}
                  {product.isSelected && (
                    <>
                      {/* Total weight / SKUs qty row */}
                      <div className='grid grid-cols-2 gap-2'>
                        <div>
                          <label className='block text-xs text-gray-500 mb-1'>
                            Total weight
                          </label>
                          <div className='text-sm text-gray-400'>
                            {product.weight} kg
                          </div>
                        </div>
                        <div>
                          <label className='block text-xs text-gray-500 mb-1'>
                            SKUs qty
                          </label>
                          <div className='flex items-center gap-1'>
                            <button
                              type='button'
                              onClick={() =>
                                handleQuantityChange(product.id, -1)
                              }
                              className='p-1 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50'
                              disabled={product.selectedQty <= 0}
                            >
                              <MinusIcon className='h-3 w-3' />
                            </button>
                            <input
                              type='text'
                              value={product.selectedQty}
                              readOnly
                              className='w-10 text-center text-sm border border-gray-300 rounded py-0.5'
                            />
                            <button
                              type='button'
                              onClick={() =>
                                handleQuantityChange(product.id, 1)
                              }
                              className='p-1 rounded border border-gray-300 text-gray-500 hover:bg-gray-50 disabled:opacity-50'
                              disabled={product.selectedQty >= product.inStock}
                            >
                              <PlusIcon className='h-3 w-3' />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Pallet type */}
                      <div>
                        <label className='block text-xs text-gray-500 mb-1'>
                          Pallet type
                        </label>
                        <select
                          value={product.palletType}
                          onChange={(e) =>
                            handlePalletTypeChange(product.id, e.target.value)
                          }
                          className='w-full text-sm border border-gray-300 rounded py-1 px-2'
                        >
                          {palletTypeOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  {/* View Warehouses button */}
                  <button
                    type='button'
                    onClick={() => setWarehouseModalProduct(product)}
                    className='w-full py-2 px-3 text-sm font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors'
                  >
                    View Warehouses ({product.warehouseStock.length})
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Warehouse Modal for Grid View */}
        {warehouseModalProduct && (
          <div className='fixed inset-0 z-50 flex items-center justify-center'>
            {/* Backdrop */}
            <div
              className='absolute inset-0 bg-black/30'
              onClick={() => setWarehouseModalProduct(null)}
            />
            {/* Modal */}
            <div className='relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden'>
              {/* Header */}
              <div className='flex items-start justify-between p-4 border-b border-gray-200'>
                <div>
                  <h3 className='text-lg font-semibold text-gray-900'>
                    {warehouseModalProduct.name}
                  </h3>
                  <p className='text-sm text-gray-500'>
                    SKU ID: {warehouseModalProduct.skuId}
                  </p>
                </div>
                <button
                  type='button'
                  onClick={() => setWarehouseModalProduct(null)}
                  className='text-gray-400 hover:text-gray-600'
                >
                  <XMarkIcon className='h-5 w-5' />
                </button>
              </div>

              {/* Unavailable Warning */}
              {warehouseModalProduct.isUnavailable && (
                <div className='mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2'>
                  <ExclamationCircleIcon className='h-5 w-5 text-red-500 flex-shrink-0' />
                  <span className='text-sm text-red-700'>
                    Selected product is currently unavailable in one of the
                    warehouses
                  </span>
                </div>
              )}

              {/* Warehouse Table */}
              <div className='p-4 overflow-auto'>
                {/* Table Header */}
                <div className='grid grid-cols-[1fr_80px_1fr_90px_90px] gap-4 text-xs font-medium text-gray-500 mb-2'>
                  <div className='flex items-center gap-1'>
                    Warehouse
                    <ChevronDownIcon className='h-3 w-3' />
                  </div>
                  <div className='flex items-center gap-1'>
                    Stored qty
                    <ChevronDownIcon className='h-3 w-3' />
                  </div>
                  <div className='flex items-center gap-1'>
                    Address
                    <ChevronDownIcon className='h-3 w-3' />
                  </div>
                  <div className='flex items-center gap-1'>
                    Availability
                    <ChevronDownIcon className='h-3 w-3' />
                  </div>
                  <div className='flex items-center gap-1'>
                    Status
                    <ChevronDownIcon className='h-3 w-3' />
                  </div>
                </div>

                {/* Warehouse Rows */}
                {warehouseModalProduct.warehouseStock.map((warehouse) => (
                  <div
                    key={warehouse.warehouseId}
                    className='grid grid-cols-[1fr_80px_1fr_90px_90px] gap-4 py-3 border-t border-gray-200 text-sm'
                  >
                    <div className='text-gray-900'>
                      {warehouse.warehouseName}
                    </div>
                    <div className='text-gray-900'>{warehouse.storedQty}</div>
                    <div className='text-gray-500'>{warehouse.address}</div>
                    <div>
                      <AvailabilityBadge availability={warehouse.availability} />
                    </div>
                    <div>
                      <StatusBadge status={warehouse.status} />
                    </div>
                  </div>
                ))}

                {warehouseModalProduct.warehouseStock.length === 0 && (
                  <div className='py-8 text-center text-gray-500'>
                    No warehouse data available
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className='flex items-center gap-2 text-sm text-gray-500'>
          <button
            type='button'
            className='text-gray-400 hover:text-gray-600'
            onClick={() => {
              const allDeselected = products.map((p) => ({
                ...p,
                isSelected: false,
              }));
              setProducts(allDeselected);
              onProductsChange(allDeselected);
            }}
          >
            ✕
          </button>
          <span>Selected: {selectedCount}</span>
        </div>
      </div>

      {/* Right Column - Load Summary */}
      <div className='flex flex-col gap-4'>
        <div className='bg-white rounded-lg border border-gray-200 p-4'>
          <h3 className='text-base font-semibold text-gray-900 mb-1'>
            Load Summary
          </h3>
          <p className='text-xs text-gray-500 mb-4'>
            Totals automatically recalculate.
          </p>

          {/* Summary Stats */}
          <div className='space-y-3 mb-6'>
            <div className='flex justify-between text-sm'>
              <span className='text-gray-500'>Total SKUs</span>
              <span className='text-gray-900 font-medium'>
                {loadSummary.totalSKUs}
              </span>
            </div>
            <div className='flex justify-between text-sm'>
              <span className='text-gray-500'>Total units</span>
              <span className='text-gray-900 font-medium'>
                {loadSummary.totalUnits}
              </span>
            </div>
            <div className='flex justify-between text-sm'>
              <span className='text-gray-500'>∧ Total pallets</span>
              <span className='text-gray-900 font-medium'>
                {loadSummary.totalPallets} pallets
              </span>
            </div>
          </div>

          {/* Pallet Breakdown */}
          {loadSummary.palletBreakdown.map((pallet) => (
            <div
              key={pallet.type}
              className='border-t border-gray-100 pt-3 pb-3'
            >
              <div className='flex justify-between items-start mb-2'>
                <span className='text-sm font-medium text-gray-900'>
                  {getPalletTypeName(pallet.type)}
                </span>
                <span className='text-xs text-gray-500'>
                  {pallet.dimensions}
                </span>
              </div>
              <div className='grid grid-cols-3 gap-2 text-xs'>
                <div>
                  <p className='text-gray-500'>Quantity</p>
                  <p className='text-gray-900 font-medium'>{pallet.quantity}</p>
                </div>
                <div>
                  <p className='text-gray-500'>Weight</p>
                  <p className='text-gray-900 font-medium'>
                    {pallet.weight} kg
                  </p>
                </div>
                <div>
                  <p className='text-gray-500'>Volume</p>
                  <p className='text-gray-900 font-medium'>
                    {pallet.volume.toFixed(1)} m³
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Total Weight */}
          <div className='border-t border-gray-100 pt-3 flex justify-between text-sm'>
            <span className='text-gray-500'>Total weight</span>
            <span className='text-gray-900 font-semibold'>
              {loadSummary.totalWeight.toLocaleString()} kg
            </span>
          </div>
        </div>

        {/* Unavailable Products Warning */}
        {loadSummary.unavailableProducts.length > 0 && (
          <div className='bg-red-50 rounded-lg border border-red-200 p-4'>
            <div className='flex items-center gap-2 mb-3'>
              <ExclamationCircleIcon className='h-5 w-5 text-red-500' />
              <span className='text-sm font-medium text-red-700'>
                Currently unavailable product(s):
              </span>
            </div>

            {loadSummary.unavailableProducts.map((product) => (
              <div
                key={product.id}
                className='flex items-center gap-3 bg-white rounded-lg p-2 mt-2'
              >
                <div className='h-10 w-10 bg-gray-100 rounded flex items-center justify-center overflow-hidden'>
                  {product.image ? (
                    <img
                      src={product.image}
                      alt={product.name}
                      className='h-full w-full object-cover'
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <span className='text-xs text-gray-400'>IMG</span>
                  )}
                </div>
                <div>
                  <p className='text-sm font-medium text-gray-900'>
                    {product.name}
                  </p>
                  <p className='text-xs text-gray-500'># {product.skuId}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
